<template>
  <section class="sec_welcome">
    <PartsLayoutContainer>
      <PartsSubPageTitle :title="title" title-modifier="my-page-title" />
    </PartsLayoutContainer>

    <div class="welcome-wrapper">
      <!-- 導入。支給はここだけ `.container` の外に出して画像を全幅で敷く -->
      <div class="welcome-head">
        <!-- eslint-disable-next-line vue/no-v-html -- 支給HTMLが出どころで、ユーザー入力は混ざらない -->
        <p class="welcome-head_title" v-html="WELCOME_HEAD.title"></p>
        <!-- eslint-disable-next-line vue/no-v-html -- 同上 -->
        <p class="welcome-head_text" v-html="WELCOME_HEAD.text"></p>
        <img :src="WELCOME_HEAD.image" class="welcome-head_img pc-only" alt="" />
        <img :src="WELCOME_HEAD.spImage" class="welcome-head_img sp-only" alt="" />
      </div>

      <PartsLayoutContainer>
        <!-- 3つのポイント -->
        <div class="welcome-support">
          <div class="welcome-support_title">
            <p>
              <!-- eslint-disable-next-line vue/no-v-html -- 同上 -->
              <span v-html="WELCOME_SUPPORT_TITLE"></span>
              <span class="welcome-support_title-num">3</span>
              <span class="welcome-support_title-text">つの</span>
              <span class="welcome-support_title-support-text">ポイント</span>
            </p>
          </div>

          <div class="welcome-support_list">
            <template v-for="item in WELCOME_SUPPORT_ITEMS" :key="item.point">
              <!-- 1〜3件目。番号を PC と SP で別々に置くのは支給どおり -->
              <div v-if="item.variant !== 'faq'" class="welcome-support_item">
                <p class="welcome-support_item_point pc-only">
                  Point <span>{{ item.point }}</span>
                </p>
                <div class="welcome-support_item_body">
                  <p class="welcome-support_item_point sp-only">
                    Point <span>{{ item.point }}</span>
                  </p>
                  <div class="welcome-support_item_icon">
                    <img :src="item.icon" alt="" />
                  </div>
                  <!-- eslint-disable-next-line vue/no-v-html -- 同上 -->
                  <h3 class="welcome-support_item_title" v-html="item.title"></h3>
                  <!-- eslint-disable-next-line vue/no-v-html -- 同上 -->
                  <p class="welcome-support_item_text" v-html="item.text"></p>
                  <OrganismsWelcomeSupportButton :button="item.button" />
                </div>
              </div>

              <!-- 4件目。アイコンと見出しを `_faq_title-box` で括り、本文とボタンを右に回す -->
              <div v-else class="welcome-support_item welcome-support_item_faq">
                <p class="welcome-support_item_point sp-only">
                  Point <span>{{ item.point }}</span>
                </p>
                <div class="welcome-support_faq_body">
                  <div class="welcome-support_faq_title-box">
                    <div class="welcome-support_item_icon">
                      <img :src="item.icon" alt="" />
                    </div>
                    <!-- eslint-disable-next-line vue/no-v-html -- 同上 -->
                    <h3 class="welcome-support_item_title" v-html="item.title"></h3>
                  </div>
                  <div class="welcome-support_faq_text-btn">
                    <!-- eslint-disable-next-line vue/no-v-html -- 同上 -->
                    <p class="welcome-support_item_text" v-html="item.text"></p>
                    <OrganismsWelcomeSupportButton :button="item.button" />
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- 早速商品を探す -->
        <div class="product-search">
          <div class="product-search_title">
            <p>早速商品<span>を</span>探す</p>
          </div>
          <div class="product-search_list">
            <NuxtLink
              v-for="card in WELCOME_CATEGORY_CARDS"
              :key="card.categoryId"
              :to="categoryLink(card.categoryId)"
              class="product-search_item"
            >
              <div class="product-search_item_info">
                <img :src="card.icon" alt="" class="product-search_item_icon" />
                <p class="product-search_item_name">
                  {{ categoryName(card.categoryId) }}
                  <span class="product-search_item_arrow" aria-hidden="true"></span>
                </p>
              </div>
              <div class="product-search_item_img">
                <img :src="card.image" :alt="categoryName(card.categoryId)" />
              </div>
            </NuxtLink>
          </div>
        </div>

        <!-- お客様の声から探す -->
        <div class="voice-search">
          <div class="product-search_title">
            <p>お客様の声<span>から</span>探す</p>
          </div>
          <div class="voice-search_list">
            <div class="voice-search_img">
              <img :src="WELCOME_VOICE_SEARCH.image" class="pc-only" alt="" />
              <img :src="WELCOME_VOICE_SEARCH.spImage" class="sp-only" alt="" />
            </div>
            <NuxtLink :to="WELCOME_VOICE_SEARCH.button.to" class="voice-search_btn">
              <p>
                {{ WELCOME_VOICE_SEARCH.button.label }}
                <span class="voice-search_arrow" aria-hidden="true"></span>
              </p>
            </NuxtLink>
          </div>
        </div>
      </PartsLayoutContainer>
    </div>
  </section>
</template>

<script setup lang="ts">
/**
 * 初めての方へ。支給デザイン `welcome.html` の `section.sec_welcome`。
 *
 * **支給は `.container` を入れ子にしている** — 見出しで1つ、`welcome-wrapper` の中でもう1つ。
 * 間に挟まる `welcome-head` だけが枠の外に出て、画像を全幅で敷く（`renewal/style.css:9111`）。
 *
 * 見出し・本文は支給HTML をそのまま `v-html` で出す（改行の出し分けを保つため。
 * 詳細は `WelcomeConst.ts` の docblock）。
 */
import { PRODUCT_CONTENT_CONSTANTS, CATEGORY_DISPLAY_ORDER } from '@/consts/ProductContentConst';
import { PRODUCTS_ANCHOR_HASH, PRODUCTS_CATEGORY_QUERY } from '@/consts/ChromeConst';
import {
  WELCOME_HEAD,
  WELCOME_SUPPORT_TITLE,
  WELCOME_SUPPORT_ITEMS,
  WELCOME_CATEGORY_CARDS,
  WELCOME_VOICE_SEARCH,
} from '@/consts/WelcomeConst';

type Props = {
  title: string;
};

defineProps<Props>();

/** 表示名は `ProductContentConst` が正。const には `categoryId` しか持たせない */
const categoryName = (id: number): string =>
  PRODUCT_CONTENT_CONSTANTS.CATEGORY_JAPANESE_NAME[id] ?? '';

/**
 * トップの「人気の商品ランキング」へ、該当タブを選んだ状態で飛ばす。
 * ハンバーガー（`organisms/layouts/header/index.vue:110-114`）と同じ組み立て方に揃える。
 */
const categoryLink = (id: number) => ({
  path: '/',
  query: { [PRODUCTS_CATEGORY_QUERY]: PRODUCT_CONTENT_CONSTANTS.CATEGORY_ENGLISH_NAME[id] },
  hash: PRODUCTS_ANCHOR_HASH,
});

// 支給の並びと `CATEGORY_DISPLAY_ORDER` が一致していることを開発時に気づけるようにする
if (import.meta.dev) {
  const order = WELCOME_CATEGORY_CARDS.map((c) => c.categoryId).join(',');
  if (order !== CATEGORY_DISPLAY_ORDER.join(',')) {
    console.warn(`[welcome] カテゴリの並びが CATEGORY_DISPLAY_ORDER と違う: ${order}`);
  }
}
</script>
